// eslint-disable-next-line no-unused-vars
import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { motion } from "framer-motion";

// Import extracted components
import AuthModal from "./components/auth/AuthModal";
import EnrollmentForm from "./components/forms/EnrollmentForm";
import DashboardButton from "./components/dashboard/DashboardButton";
import AdminButton from "./components/dashboard/AdminButton";
import StudentPaymentsModal from "./components/payments/StudentPaymentsModal";
import AdminPaymentsModal from "./components/payments/AdminPaymentsModal";
import FeatureCard from "./components/ui/FeatureCard";
import CountdownTimer, { COUNTDOWN_DEFAULT_STYLE } from "./components/ui/CountdownTimer";
import LangSelect from "./components/ui/LangSelect";
import TrainingProgress from "./components/training/TrainingProgress";
import AdminTrainingProgress from "./components/training/AdminTrainingProgress";
import InstructorDashboard from "./components/dashboard/InstructorDashboard";
import StudyMaterialsModal from "./components/materials/StudyMaterialsModal";
import StudentStudyMaterialsModal from "./components/materials/StudentStudyMaterialsModal";
import DocumentsModal from "./components/documents/DocumentsModal";
import StudentDocumentsModal from "./components/documents/StudentDocumentsModal";
// Import API_BASE helper
import ActiveSessionsModal from "./components/admin/ActiveSessionsModal";
import UserManagementModal from "./components/admin/UserManagementModal";
import { API_BASE, training, payments as paymentsApi } from './lib/api';
import QuestionBankModal from "./components/admin/QuestionBankModal";
import EnrollmentMessagesModal from "./components/admin/EnrollmentMessagesModal";
import { mergeDeep, cloneDeep, formatMessage } from './utils/i18n';
import { cloneTimerForm, mapTimerSettingsToForm, toIsoStringFromLocalInput } from './utils/timerUtils';
import AppointmentManagementModal from "./components/admin/AppointmentManagementModal";
import { T } from './i18n/main';
import TrainingCalendarModal from "./components/admin/TrainingCalendarModal";
import { TRAINING_PROGRESS_COPY, DEFAULT_SCHEDULE_COPY, SCHEDULE_TRAINING_COPY } from './i18n/training';
import SystemSettingsModal from "./components/admin/SystemSettingsModal";
import { DOCUMENTS_COPY, STUDENT_DOCUMENTS_COPY } from './i18n/documents';
import TrainingScheduleModal from "./components/training/TrainingScheduleModal";
import { STUDY_MATERIALS_COPY, STUDENT_STUDY_MATERIALS_COPY } from './i18n/materials';
import ContentManagementModal from "./components/admin/ContentManagementModal";
import { STUDENT_PAYMENTS_COPY } from './i18n/payments';
import {
  DOCUMENT_TYPE_OPTIONS,
  LEGACY_DOCUMENT_KIND_TO_NEW,
  REQUIRED_DOCUMENT_KINDS,
  DOCUMENT_STATUS_META,
  normalizeDocumentKind,
  getDocumentStatusMeta,
  buildDocumentRequirementsSummary,
  studentDocumentsRequireAttention,
  countPendingRequiredDocuments,
  countPendingAppointments,
  buildDocumentTypeLabelMap,
  formatDocumentKindLabel
} from './utils/documentUtils';


// === CSS STYLES FOR HIGHLIGHTING ===
const highlightStyles = `
  .adminHighlight {
    background: #ffff00 !important;
    padding: 2px 6px !important;
    border-radius: 4px !important;
    font-weight: inherit !important;
    font-family: inherit !important;
    font-size: inherit !important;
    font-style: inherit !important;
    line-height: inherit !important;
    text-decoration: inherit !important;
    letter-spacing: inherit !important;
    color: inherit !important;
    box-shadow: 0 2px 4px rgba(0,0,0,0.1) !important;
    display: inline !important;
    vertical-align: baseline !important;
    text-transform: inherit !important;
    font-variant: inherit !important;
    font-stretch: inherit !important;
    font-kerning: inherit !important;
    font-feature-settings: inherit !important;
    font-variant-ligatures: inherit !important;
    font-variant-caps: inherit !important;
    font-variant-numeric: inherit !important;
    font-variant-east-asian: inherit !important;
  }
`;

const ADMIN_SECTION_KEY_BY_ID = {
  'user-management': 'userManagement',
  'question-bank': 'questionBank',
  'training-progress': 'trainingProgress',
  'system-settings': 'systemSettings',
  'payments': 'payments',
  'study-materials': 'studyMaterials',
  'student-documents': 'studentDocuments',
  'enrollment-messages': 'enrollmentMessages',
  'content-management': 'contentManagement',
  'training-calendar': 'trainingCalendar',
};

const ADMIN_ICON_SCALE = {
  progress: 0.9,
  payments: 1,
  'study-materials': 1.4,
  documents: 1.1,
  schedule: 1.35,
  permit: 1.13,
  users: 0.86,
  'question-bank': 0.84,
  settings: 0.84,
  content: 0.84,
};

const adminIcon = (name, alt) => (
  <img
    src={`/icons/${name}.png`}
    alt={alt}
    className="w-20 h-20 object-contain block"
    style={{ transform: `scale(${ADMIN_ICON_SCALE[name] || 1})` }}
    loading="lazy"
  />
);

const ADMIN_ICON_BY_ID = {
  'user-management': adminIcon('users', 'User Management'),
  'question-bank': adminIcon('question-bank', 'Question Bank'),
  'training-progress': adminIcon('progress', 'Training Progress'),
  'system-settings': adminIcon('settings', 'System Settings'),
  'payments': adminIcon('payments', 'Payments & Discounts'),
  'study-materials': adminIcon('study-materials', 'Study Materials'),
  'student-documents': adminIcon('documents', 'Student Documents'),
  'training-calendar': adminIcon('schedule', 'Training Calendar'),
  'enrollment-messages': adminIcon('permit', 'Enrollment Messages'),
  'content-management': adminIcon('content', 'Content Management'),
};

const getAdminIcon = (id, fallback) => ADMIN_ICON_BY_ID[id] || fallback;

function formatRoleLabel(role) {
  if (!role) return 'User';
  return String(role)
    .toLowerCase()
    .split(/[_\s-]+/)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');
}

function getUserDisplayName(user) {
  if (!user) return '';
  const nameParts = [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  if (nameParts) return nameParts;
  if (user.name) return user.name;
  return user.email || '';
}

function buildRoleNameTitle(user) {
  if (!user) return '';
  const roleLabel = formatRoleLabel(user.role);
  const displayName = getUserDisplayName(user);
  if (roleLabel && displayName) return `${roleLabel} - ${displayName}`;
  if (displayName) return displayName;
  return roleLabel;
}

function buildAdminSection(id, icon, fallbackTitle, fallbackDescription, sectionCopy = {}) {
  const key = ADMIN_SECTION_KEY_BY_ID[id];
  const copy = key ? sectionCopy[key] : null;
  return {
    id,
    icon,
    title: copy?.title || fallbackTitle,
    description: copy?.description || fallbackDescription,
  };
}

// Helper functions for dashboard ordering
function getOrderedAdminSections(sectionCopy) {
  const defaultSections = [
    buildAdminSection(
      "user-management",
      getAdminIcon("user-management", "👥"),
      "User Management",
      "View, edit, and manage student accounts and instructor profiles",
      sectionCopy
    ),
    buildAdminSection(
      "question-bank",
      getAdminIcon("question-bank", "📚"),
      "Question Bank",
      "Add, edit, and manage CDL practice questions across all categories",
      sectionCopy
    ),
    buildAdminSection(
      "training-progress",
      getAdminIcon("training-progress", "📊"),
      "Training Progress",
      "View and manage student training progress across all sections",
      sectionCopy
    ),
    buildAdminSection(
      "system-settings",
      getAdminIcon("system-settings", "⚙️"),
      "System Settings",
      "Configure platform settings, categories, and system preferences",
      sectionCopy
    ),
    buildAdminSection(
      "payments",
      getAdminIcon("payments", "💰"),
      "Payments & Discounts",
      "Review student balances, approve payments, and manage discounts",
      sectionCopy
    ),
    buildAdminSection(
      "study-materials",
      getAdminIcon("study-materials", "📖"),
      "Study Materials",
      "Upload, manage, and organize training documents and resources",
      sectionCopy
    ),
    buildAdminSection(
      "student-documents",
      getAdminIcon("student-documents", "📋"),
      "Student Documents",
      "View and manage documents uploaded by students",
      sectionCopy
    ),
    buildAdminSection(
      "enrollment-messages",
      getAdminIcon("enrollment-messages", "📝"),
      "Enrollment Messages",
      "View and manage student enrollment requests",
      sectionCopy
    ),
    buildAdminSection(
      "content-management",
      getAdminIcon("content-management", "📄"),
      "Content Management",
      "Manage website content and announcements",
      sectionCopy
    ),
    buildAdminSection(
      "training-calendar",
      getAdminIcon("training-calendar", "📅"),
      "Training Calendar",
      "View weekly calendar organized by trucks with student names and manage appointments",
      sectionCopy
    )
  ];

  const savedOrder = localStorage.getItem('admin-dashboard-order');
  if (savedOrder) {
    try {
      const order = JSON.parse(savedOrder);
      if (!Array.isArray(order)) return defaultSections;

      const orderedSections = order
        .map(id => defaultSections.find(s => s.id === id))
        .filter(Boolean);
      const missingSections = defaultSections.filter(section => !order.includes(section.id));
      return [...orderedSections, ...missingSections];
    } catch (error) {
      console.error('Failed to parse admin dashboard order:', error);
    }
  }
  return defaultSections;
}

function getOrderedStudentSections(t) {
  const studentIcon = (name, alt) => (
    <img
      src={`/icons/${name}.png`}
      alt={alt}
      className="w-24 h-24 object-contain block"
      loading="lazy"
    />
  );

  const descriptionCopy = t.dashboardDescriptions || {};
  const defaultSections = [
    {
      id: "permit",
      title: t.dashboard.permit,
      description: descriptionCopy.permit || "Practice for your CDL permit exam with interactive questions",
      icon: studentIcon('permit', t.dashboard.permit),
    },
    {
      id: "schedule",
      title: t.dashboard.schedule,
      description: descriptionCopy.schedule || "Book your hands-on training sessions with instructors",
      icon: studentIcon('schedule', t.dashboard.schedule),
    },
    {
      id: "progress",
      title: t.dashboard.progress,
      description: descriptionCopy.progress || "Track your training progress and completion status",
      icon: studentIcon('progress', t.dashboard.progress)
    },
    {
      id: "documents",
      title: t.dashboard.documents,
      description: descriptionCopy.documents || "Access your training certificates and important documents",
      icon: studentIcon('documents', t.dashboard.documents)
    },
    {
      id: "study-materials",
      title: t.dashboard.studyMaterials,
      description: descriptionCopy.studyMaterials || "Access training materials, guides, and resources",
      icon: studentIcon('study-materials', t.dashboard.studyMaterials)
    },
    {
      id: "payments",
      title: t.dashboard.payments,
      description: descriptionCopy.payments || "View your tuition balance, generate discounts, and submit payments for approval",
      icon: studentIcon('payments', t.dashboard.payments)
    }
  ];

  const savedOrder = localStorage.getItem('student-dashboard-order');
  if (savedOrder) {
    try {
      const order = JSON.parse(savedOrder);
      if (!Array.isArray(order)) return defaultSections;

      const orderedSections = order
        .map(id => defaultSections.find(s => s.id === id))
        .filter(Boolean);
      const missingSections = defaultSections.filter(section => !order.includes(section.id));
      return [...orderedSections, ...missingSections];
    } catch (error) {
      console.error('Failed to parse student dashboard order:', error);
    }
  }
  return defaultSections;
}

// Inject styles into document head
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = highlightStyles;
  document.head.appendChild(styleElement);
}

function highlightText(text, phrase, className) {
  if (!phrase || !phrase.trim()) return text;

  const regex = new RegExp(`(${phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
  return text.replace(regex, `<span class="${className}">$1</span>`);
}

// Debug logs
console.log("🚀 API_BASE:", API_BASE);
console.log("🔧 Build timestamp:", new Date().toISOString());
console.log("🔥 CACHE BUST:", Math.random());

// --- JWT token management with localStorage ---
const TOKEN_KEY = 'go2cdl_access_token';
const USER_KEY = 'go2cdl_user';

// Access token getter that supports both legacy and new keys
const getAccessToken = () =>
  (typeof localStorage !== 'undefined' && (
    localStorage.getItem('accessToken') ||
    localStorage.getItem(TOKEN_KEY)
  )) || '';

const persistAccessToken = (token) => {
  if (token) {
    console.log('Setting access token in localStorage');
    localStorage.setItem(TOKEN_KEY, token);
  } else {
    console.log('Clearing access token from localStorage');
    localStorage.removeItem(TOKEN_KEY);
  }
};

const TRAINING_TIME_SLOTS = (() => {
  const slots = [];
  for (let hour = 8; hour <= 19; hour += 1) {
    slots.push({ label: `${String(hour).padStart(2, '0')}:00`, minutes: hour * 60 });
  }
  return slots;
})();

const toWeekStart = (date) => {
  const d = new Date(date);
  d.setHours(0, 0, 0, 0);
  const currentDay = d.getDay();
  const diff = (currentDay === 0 ? -6 : 1) - currentDay;
  d.setDate(d.getDate() + diff);
  return d;
};

const formatDayLabel = (date) => date.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });

const formatDateTimeDisplay = (date) => date.toLocaleString(undefined, { weekday: 'short', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });

const formatTimeRange = (start, end) => `${start.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })} - ${end.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' })}`;

const combineDateAndMinutes = (day, minutes) => {
  const result = new Date(day);
  result.setHours(0, 0, 0, 0);
  result.setMinutes(minutes);
  return result;
};

const persistUserProfile = (user) => {
  if (user) {
    console.log('Setting user in localStorage:', user);
    localStorage.setItem(USER_KEY, JSON.stringify(user));
  } else {
    console.log('Clearing user from localStorage');
    localStorage.removeItem(USER_KEY);
  }
};

// --- API_BASE helpers (inline to keep single-file for now) ---

/* ------ EXAMS API_BASE HELPERS ------ */
async function apiExamCategories() {
  const r = await fetch(`${API_BASE}/api/exams/categories`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}` },
    credentials: "include",
  });
  if (!r.ok) throw new Error("Failed to load exam categories");
  return r.json(); // ["General Knowledge", "Air Brakes", ...]
}

async function apiExamQuestions(category) {
  const r = await fetch(`${API_BASE}/api/exams/questions/${encodeURIComponent(category)}`, {
    headers: { Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}` },
    credentials: "include",
  });
  if (!r.ok) throw new Error("Failed to load questions");
  return r.json(); // [{id, text, choices, ...}]
}

async function apiExamAnswer(questionId, choice) {
  const r = await fetch(`${API_BASE}/api/exams/answer`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
    },
    credentials: "include",
    body: JSON.stringify({ questionId, choice }),
  });
  if (!r.ok) throw new Error("Failed to submit answer");
  return r.json(); // { correct, correctIndex, explanation }
}







// Image URLs - replace with your own images later
// const TRAINING_IMG = "https://images.unsplash.com/photo-1544816155-12df9643f363?q=80&w=1200&auto=format&fit=crop";
// const INSTRUCTOR_IMG = "https://images.unsplash.com/photo-1541534401786-2077eed87a72?q=80&w=1200&auto=format&fit=crop";
// const PLACEMENT_IMG = "https://images.unsplash.com/photo-1590681644699-1ce1884957aa?q=80&w=1200&auto=format&fit=crop";

const LANGS = [
  { code: "en", label: "English" },
  { code: "uk", label: "Українська" },
  { code: "ru", label: "Русский" },
  { code: "es", label: "Español" },
];




export default function App() {
  const [lang, setLang] = useState("en");
  const [user, setUser] = useState(null); // {email, role}
  const [showAuth, setShowAuth] = useState(false);
  const [authMode, setAuthMode] = useState("login");
  const referralCodeFromUrl = useMemo(() => {
    if (typeof window === 'undefined') return '';
    return (new URLSearchParams(window.location.search).get('ref') || '').trim().toUpperCase();
  }, []);
  const [showExamPractice, setShowExamPractice] = useState(false);
  const [unreadEnrollments, setUnreadEnrollments] = useState(0);
  const [showInstructorTrainingProgress, setShowInstructorTrainingProgress] = useState(false);
  const [showInstructorStudyMaterials, setShowInstructorStudyMaterials] = useState(false);
  const [showInstructorStudentDocuments, setShowInstructorStudentDocuments] = useState(false);
  const [showInstructorTrainingCalendar, setShowInstructorTrainingCalendar] = useState(false);
  const [showMessenger, setShowMessenger] = useState(false);
  const [messengerMessages, setMessengerMessages] = useState([]);
  const [messengerPartner, setMessengerPartner] = useState(null);
  const [messengerError, setMessengerError] = useState("");
  const [messengerLoading, setMessengerLoading] = useState(false);
  const [messengerText, setMessengerText] = useState("");
  const [messengerRecipients, setMessengerRecipients] = useState([]);
  const [messengerThreads, setMessengerThreads] = useState([]);
  const [messengerUnreadTotal, setMessengerUnreadTotal] = useState(0);
  const messengerCacheRef = useRef(new Map());
  const [phonePrompted, setPhonePrompted] = useState(false);
  const [selectedRecipientId, setSelectedRecipientId] = useState(null);
  const [timerSettings, setTimerSettings] = useState(null);
  const [timerLoaded, setTimerLoaded] = useState(false);

  // Persist language selection in localStorage
  useEffect(() => {
    const saved = localStorage.getItem("go2_lang");
    if (saved && T[saved]) setLang(saved);
  }, []);
  useEffect(() => {
    localStorage.setItem("go2_lang", lang);
  }, [lang]);

  // A referral QR code opens the signup form with its referral value already filled in.
  useEffect(() => {
    if (!referralCodeFromUrl || typeof window === 'undefined') return;
    const hasSavedSession = Boolean(
      localStorage.getItem('go2cdl_access_token') || localStorage.getItem('accessToken')
    );
    if (!hasSavedSession) {
      setAuthMode('signup');
      setShowAuth(true);
    }
  }, [referralCodeFromUrl]);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();

    const loadTimerSettings = async () => {
      try {
        const response = await fetch(`${API_BASE}/api/site/timer`, { signal: controller.signal });
        if (!response.ok) {
          throw new Error(`Request failed: ${response.status}`);
        }

        const data = await response.json();
        if (!active) return;

        const styleFromServer = data?.style_json && typeof data.style_json === 'object'
          ? data.style_json
          : {};

        setTimerSettings({
          timer_enabled: Boolean(data?.timer_enabled),
          timer_text: data?.timer_text || 'Next CDL class starts in:',
          target_date: data?.target_date || null,
          expire_action: data?.expire_action || 'hide',
          style_json: { ...COUNTDOWN_DEFAULT_STYLE, ...styleFromServer },
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Failed to load timer settings:', error);
        }
      } finally {
        if (active) {
          setTimerLoaded(true);
        }
      }
    };

    loadTimerSettings();

    return () => {
      active = false;
      controller.abort();
    };
  }, []);

  useEffect(() => {
    if (typeof window === 'undefined') {
      return undefined;
    }

    const handleUpdated = (event) => {
      const data = event?.detail || {};
      setTimerSettings({
        timer_enabled: Boolean(data.timer_enabled),
        timer_text: data.timer_text || 'Next CDL class starts in:',
        target_date: data.target_date || null,
        expire_action: data.expire_action || 'hide',
        style_json: { ...COUNTDOWN_DEFAULT_STYLE, ...(data.style_json || {}) },
      });
      setTimerLoaded(true);
    };

    window.addEventListener('timer-settings:updated', handleUpdated);
    return () => {
      window.removeEventListener('timer-settings:updated', handleUpdated);
    };
  }, [setTimerLoaded, setTimerSettings]);

  // Session restoration on app load
  useEffect(() => {
    console.log('🔄 App starting - attempting session restoration...');

    const restoreSession = () => {
      try {
        // Simple approach: try localStorage first
        const token = localStorage.getItem('go2cdl_access_token');
        const savedUser = localStorage.getItem('go2cdl_user');

        console.log('🔍 Session check - Token exists:', !!token, 'User exists:', !!savedUser);

        if (token && savedUser) {
          try {
            const userData = JSON.parse(savedUser);
            console.log('📋 Parsed user data:', userData);

            if (userData && userData.email && userData.role) {
              console.log('✅ Session found for:', userData.email);
              setUser(userData);

              // Validate with server in background
              validateWithServer(token, userData);
              return;
            }
          } catch (parseError) {
            console.error('❌ Failed to parse user data:', parseError);
          }
        }

        console.log('❌ No valid session found');
        setUser(null);
      } catch (error) {
        console.error('❌ Session restoration error:', error);
        setUser(null);
      }
    };

    const validateWithServer = async (token, currentUser) => {
      try {
        console.log('🔍 Validating session with server...');
        const response = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${token}` },
          credentials: "include"
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.user) {
            console.log('✅ Server validation successful');
            setUser(data.user);
          } else {
            console.log('⚠️ Server returned invalid response format');
          }
        } else {
          console.log('❌ Server validation failed:', response.status);
          if (response.status === 401 || response.status === 404) {
            console.log('❌ Clearing invalid session');
            localStorage.removeItem('go2cdl_access_token');
            localStorage.removeItem('go2cdl_user');
            setUser(null);
          }
        }
      } catch (error) {
        console.error('❌ Server validation error:', error);
        // Don't clear session on network errors
      }
    };

    restoreSession();
  }, []);

  // Prompt students without phone numbers (triggered by admin)
  useEffect(() => {
    if (!user || user.role !== 'STUDENT') {
      setPhonePrompted(false);
      return;
    }

    const needsPhone = (u) => !u.phone || !String(u.phone).trim();

    const promptForPhone = async () => {
      const phoneInput = window.prompt('Please provide your Phone number.');
      if (!phoneInput || !phoneInput.trim()) return;
      try {
        const response = await fetch(`${API_BASE}/auth/users/me/phone`, {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${getAccessToken()}`,
          },
          credentials: 'include',
          body: JSON.stringify({ phone: phoneInput.trim() }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.error || 'Failed to update phone');
        const updatedUser = { ...user, phone: data.user.phone, phonePromptRequired: false };
        setUser(updatedUser);
        persistUserProfile(updatedUser);
        setPhonePrompted(true);
      } catch (err) {
        console.error('Failed to update phone:', err);
        alert(err.message || 'Failed to update phone');
      }
    };

    const checkPrompt = async () => {
      try {
        const res = await fetch(`${API_BASE}/auth/me`, {
          headers: { Authorization: `Bearer ${getAccessToken()}` },
          credentials: 'include',
        });
        if (!res.ok) return;
        const data = await res.json();
        if (data?.user) {
          setUser(data.user);
          persistUserProfile(data.user);
          if (data.user.phonePromptRequired && needsPhone(data.user) && !phonePrompted) {
            await promptForPhone();
          }
        }
      } catch (err) {
        console.error('Phone prompt check failed:', err);
      }
    };

    if (user.phonePromptRequired && needsPhone(user) && !phonePrompted) {
      promptForPhone();
    }

    const interval = setInterval(checkPrompt, 10000);
    return () => clearInterval(interval);
  }, [user, phonePrompted]);

  // Fetch unread enrollments count for admin dashboard
  useEffect(() => {
    if (user && user.role === 'ADMIN') {
      const fetchUnreadCount = async () => {
        try {
          console.log('🔍 Fetching enrollments for unread count...');
          const response = await fetch(`${API_BASE}/api/enrollment`, {
            headers: {
              Authorization: `Bearer ${getAccessToken()}`,
            },
            credentials: 'include',
          });
          if (response.ok) {
            const enrollments = await response.json();
            console.log('📧 Enrollments received:', enrollments);
            const unreadCount = enrollments.filter(e => e.status === 'NEW').length;
            console.log('🔴 Unread count:', unreadCount);
            setUnreadEnrollments(unreadCount);
          } else {
            console.error('❌ Failed to fetch enrollments:', response.status);
          }
        } catch (error) {
          console.error('Error fetching enrollments count:', error);
        }
      };

      fetchUnreadCount();
      // Refresh count every 30 seconds
      const interval = setInterval(fetchUnreadCount, 30000);
      return () => clearInterval(interval);
    }
  }, [user]);

  // Remove debug override if it existed

  const t = useMemo(() => T[lang], [lang]);

  const scrollTo = (id) => {
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const navLink = "text-2xl font-semibold text-slate-700 hover:text-slate-900";

  const loadMessengerRecipients = useCallback(async () => {
    if (!user || user.role !== 'ADMIN') return [];
    try {
      const response = await fetch(`${API_BASE}/api/users`, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load users');
      }
      const students = Array.isArray(data.users)
        ? data.users.filter(u => u.role === 'STUDENT')
        : [];
      setMessengerRecipients(students);
      return students;
    } catch (error) {
      console.error('Failed to load messenger recipients:', error);
      setMessengerError(error.message || 'Failed to load recipients');
      return [];
    }
  }, [user]);

  const markConversationRead = useCallback(async (targetId) => {
    if (!targetId) return;
    try {
      await fetch(`${API_BASE}/api/messages/read`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`
        },
        credentials: 'include',
        body: JSON.stringify({ withUserId: targetId })
      });
    } catch (error) {
      console.error('Failed to mark conversation read:', error);
    }
  }, []);

  const loadThreads = useCallback(async () => {
    if (!user) return;
    try {
      const response = await fetch(`${API_BASE}/api/messages/threads`, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load threads');
      }
      const threads = Array.isArray(data.threads) ? data.threads : [];
      setMessengerThreads(threads);
      setMessengerUnreadTotal(Number.isFinite(data.unreadTotal) ? data.unreadTotal : 0);

      if (user.role === 'ADMIN' && !selectedRecipientId && threads.length) {
        setSelectedRecipientId(threads[0].userId);
      }
      threads.forEach(thread => {
        const key = thread.userId || 'default';
        if (!messengerCacheRef.current.has(key) && thread.lastMessage) {
          messengerCacheRef.current.set(key, [thread.lastMessage]);
        }
      });
    } catch (error) {
      console.error('Failed to load threads:', error);
    }
  }, [user, selectedRecipientId]);

  const loadConversation = useCallback(async (targetId = null, { silent = false } = {}) => {
    if (!user) return;
    const cacheKey = targetId || selectedRecipientId || messengerPartner?.id || 'default';
    if (cacheKey && messengerCacheRef.current.has(cacheKey)) {
      setMessengerMessages(messengerCacheRef.current.get(cacheKey));
    }
    if (!silent) setMessengerLoading(true);
    setMessengerError("");
    try {
      const url = targetId
        ? `${API_BASE}/api/messages?withUser=${targetId}`
        : `${API_BASE}/api/messages`;
      const response = await fetch(url, {
        headers: { Authorization: `Bearer ${getAccessToken()}` },
        credentials: 'include'
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to load messages');
      }
      const incomingMessages = Array.isArray(data.messages) ? data.messages : [];
      const safeMessages = incomingMessages.length === 0 && messengerCacheRef.current.has(cacheKey)
        ? messengerCacheRef.current.get(cacheKey)
        : incomingMessages;
      setMessengerMessages(safeMessages);
      messengerCacheRef.current.set(cacheKey, safeMessages);
      setMessengerPartner(data.partner || null);
      if (user.role === 'ADMIN' && data.partner?.id && !selectedRecipientId) {
        setSelectedRecipientId(data.partner.id);
      }
      await markConversationRead(targetId || data.partner?.id);
      loadThreads();
    } catch (error) {
      console.error('Failed to load conversation:', error);
      setMessengerError(error.message || 'Failed to load messages');
      // If student hits admin-only error, clear partner selection
      if (user.role === 'STUDENT') {
        setMessengerPartner(null);
        setSelectedRecipientId(null);
      }
    } finally {
      if (!silent) setMessengerLoading(false);
    }
  }, [user, selectedRecipientId, messengerPartner, loadThreads, markConversationRead]);

  const handleSendMessage = useCallback(async () => {
    if (!user) return;
    const text = messengerText.trim();
    if (!text) return;

    let targetId = messengerPartner?.id || selectedRecipientId;
    if (user.role === 'ADMIN') {
      if (!targetId) {
        setMessengerError("Select a student to message.");
        return;
      }
    }
    if (user.role !== 'ADMIN' && !targetId && messengerPartner?.id) {
      targetId = messengerPartner.id;
    }

    try {
      setMessengerError("");
      const response = await fetch(`${API_BASE}/api/messages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${getAccessToken()}`
        },
        credentials: 'include',
        body: JSON.stringify({
          text,
          ...(targetId ? { toUserId: targetId } : {})
        })
      });
      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.error || 'Failed to send message');
      }
      setMessengerMessages(Array.isArray(data.messages) ? data.messages : []);
      setMessengerPartner(data.partner || messengerPartner);
      setMessengerText("");
      const cacheKey = targetId || selectedRecipientId || messengerPartner?.id || 'default';
      messengerCacheRef.current.set(cacheKey, Array.isArray(data.messages) ? data.messages : []);
      loadThreads();
    } catch (error) {
      console.error('Failed to send message:', error);
      setMessengerError(error.message || 'Failed to send message');
    }
  }, [user, messengerText, messengerPartner, selectedRecipientId, loadThreads]);

  useEffect(() => {
    if (!showMessenger || !user) return;

    const initialize = async () => {
      let targetId = selectedRecipientId;

      if (user.role === 'ADMIN') {
        const recipients = messengerRecipients.length
          ? messengerRecipients
          : await loadMessengerRecipients();
        if (!targetId && recipients && recipients.length) {
          targetId = recipients[0].id;
          setSelectedRecipientId(recipients[0].id);
        }
        if (!recipients || recipients.length === 0) {
          setMessengerError("No students available to message.");
          return;
        }
      }

      await loadConversation(targetId);
    };

    initialize();
  }, [showMessenger, user, loadMessengerRecipients, loadConversation, messengerRecipients, selectedRecipientId]);

  useEffect(() => {
    if (!user) return;
    loadThreads();
    const interval = setInterval(loadThreads, 5000);
    return () => clearInterval(interval);
  }, [user, loadThreads]);

  useEffect(() => {
    if (!showMessenger || !user) return;
    const refreshConversation = () => {
      if (selectedRecipientId || messengerPartner?.id) {
        loadConversation(selectedRecipientId || messengerPartner?.id, { silent: true });
      }
    };
    const interval = setInterval(refreshConversation, 5000);
    return () => clearInterval(interval);
  }, [showMessenger, user, selectedRecipientId, messengerPartner, loadConversation]);

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top Bar / Nav */}
      <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/90 backdrop-blur supports-[backdrop-filter]:bg-white/70">
        {/* Mobile Header */}
        <div className="mx-auto flex max-w-7xl items-center justify-between px-4 py-3 sm:px-6 lg:px-8 md:hidden">
          <a href="#home" className="flex items-center gap-2">
            <img src="/images/logo.jpg" alt="Go2 CDL Logo" className="h-9 w-16 sm:h-12 sm:w-20" />
          </a>
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMessenger(true)}
                aria-label="Message"
                title="Message"
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7l8 5 8-5" />
                </svg>
                {messengerUnreadTotal > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white animate-ping" />
                )}
              </button>
              <a href="tel:347-471-8104" aria-label="Call Go2 CDL" title="Call Go2 CDL" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.5C3 4.12 4.12 3 5.5 3h1.1c.58 0 1.09.37 1.27.92l1 3.01c.16.5.02 1.05-.36 1.41l-1.23 1.2a13.3 13.3 0 006.28 6.28l1.2-1.23c.36-.38.91-.52 1.41-.36l3.01 1c.55.18.92.69.92 1.27v1.1A2.5 2.5 0 0118.5 21C10.49 21 3 13.51 3 5.5z" />
                </svg>
              </a>
              <a href="https://wa.me/13474718104" target="_blank" rel="noopener noreferrer" aria-label="Open WhatsApp" title="Open WhatsApp" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </a>
              <a href="https://t.me/Go2_CDL" target="_blank" rel="noopener noreferrer" aria-label="Open Telegram" title="Open Telegram" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" className="h-5 w-5" fill="currentColor">
                  <path d="M120 0a120 120 0 10120 120A120.14 120.14 0 00120 0zm58.42 79.26l-19.9 94.06c-1.5 6.76-5.52 8.43-11.2 5.24l-31-22.86-14.96 14.4a7.88 7.88 0 01-6.28 3.06l2.24-31.88 57.96-52.36c2.52-2.24-.56-3.5-3.92-1.26l-71.62 45.06-30.84-9.62c-6.72-2.1-6.86-6.72 1.4-9.96l120.6-46.52c5.92-2.16 11.12 1.42 9.52 9.6z" />
                </svg>
              </a>
              <LangSelect lang={lang} setLang={setLang} LANGS={LANGS} variant="icon" />
            </div>
            {user ? (
              <button onClick={() => { setUser(null); persistAccessToken(null); localStorage.removeItem('go2cdl_access_token'); localStorage.removeItem('go2cdl_user'); }} className="rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
                {t.nav.logout}
              </button>
            ) : (
              <button onClick={() => { setAuthMode("login"); setShowAuth(true); }} className="rounded-2xl bg-red-600 px-5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-700">
                {t.nav.login}
              </button>
            )}
          </div>
        </div>

        {/* Desktop Header */}
        <div className="hidden md:flex md:items-center md:justify-between md:px-6 lg:px-8 md:py-4 mx-auto max-w-7xl">
          <a href="#home" className="flex items-center gap-2">
            <img src="/images/logo.jpg" alt="Go2 CDL Logo" className="h-12 w-20 sm:h-16 sm:w-24 md:h-20 md:w-32 lg:h-24 lg:w-36" />
          </a>
          <nav className="flex items-center gap-4">
            {!user && (
              <>
                <a href="#home" className={navLink}>{t.nav.home}</a>
                <a href="#about" className={navLink}>{t.nav.about}</a>
                <a href="#contact" className={navLink}>{t.nav.contact}</a>
              </>
            )}
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowMessenger(true)}
                aria-label="Message"
                title="Message"
                className="relative inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50"
              >
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="1.8">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16a1 1 0 011 1v10a1 1 0 01-1 1H4a1 1 0 01-1-1V7a1 1 0 011-1z" />
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 7l8 5 8-5" />
                </svg>
                {messengerUnreadTotal > 0 && (
                  <span className="absolute -top-1 -right-1 h-3 w-3 rounded-full bg-red-500 ring-2 ring-white animate-ping" />
                )}
              </button>
              <a href="tel:347-471-8104" aria-label="Call Go2 CDL" title="Call Go2 CDL" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M3 5.5C3 4.12 4.12 3 5.5 3h1.1c.58 0 1.09.37 1.27.92l1 3.01c.16.5.02 1.05-.36 1.41l-1.23 1.2a13.3 13.3 0 006.28 6.28l1.2-1.23c.36-.38.91-.52 1.41-.36l3.01 1c.55.18.92.69.92 1.27v1.1A2.5 2.5 0 0118.5 21C10.49 21 3 13.51 3 5.5z" />
                </svg>
              </a>
              <a href="https://wa.me/13474718104" target="_blank" rel="noopener noreferrer" aria-label="Open WhatsApp" title="Open WhatsApp" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="h-5 w-5" fill="currentColor">
                  <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893A11.821 11.821 0 0020.885 3.488" />
                </svg>
              </a>
              <a href="https://t.me/Go2_CDL" target="_blank" rel="noopener noreferrer" aria-label="Open Telegram" title="Open Telegram" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-slate-300 hover:bg-slate-50">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 240 240" className="h-5 w-5" fill="currentColor">
                  <path d="M120 0a120 120 0 10120 120A120.14 120.14 0 00120 0zm58.42 79.26l-19.9 94.06c-1.5 6.76-5.52 8.43-11.2 5.24l-31-22.86-14.96 14.4a7.88 7.88 0 01-6.28 3.06l2.24-31.88 57.96-52.36c2.52-2.24-.56-3.5-3.92-1.26l-71.62 45.06-30.84-9.62c-6.72-2.1-6.86-6.72 1.4-9.96l120.6-46.52c5.92-2.16 11.12 1.42 9.52 9.6z" />
                </svg>
              </a>
              <LangSelect lang={lang} setLang={setLang} LANGS={LANGS} />
            </div>
            {user ? (
              <div className="flex items-center gap-3">
                <span className="text-sm text-slate-600">{user.email} ({user.role})</span>
                <button onClick={() => { setUser(null); persistAccessToken(null); localStorage.removeItem('go2cdl_access_token'); localStorage.removeItem('go2cdl_user'); }} className="rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900">
                  {t.nav.logout}
                </button>
              </div>
            ) : (
              <button onClick={() => { setAuthMode("login"); setShowAuth(true); }} className="rounded-2xl bg-red-600 px-6 py-3 text-base font-semibold text-white shadow-sm hover:bg-red-700">
                {t.nav.login}
              </button>
            )}
          </nav>
        </div>
      </header>




      {showMessenger && user && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-2xl shadow-xl flex flex-col max-h-[90vh]">
            <div className="flex items-center justify-between p-4 border-b">
              <div>
                <h2 className="text-xl font-semibold">{t.nav.message}</h2>
                <p className="text-sm text-slate-500">
                  {messengerPartner
                    ? `Chat with ${messengerPartner.firstName || messengerPartner.email || 'recipient'}`
                    : 'Select a recipient to start chatting'}
                </p>
              </div>
              <button
                onClick={() => setShowMessenger(false)}
                className="text-slate-500 hover:text-slate-700 text-2xl leading-none"
                aria-label="Close messaging"
              >
                ×
              </button>
            </div>

            {user.role === 'ADMIN' && (
              <div className="px-4 py-3 border-b bg-slate-50">
                <div className="grid gap-3">
                  <label className="flex items-center gap-3 text-sm font-medium text-slate-700">
                    <span>Send to:</span>
                    <select
                      className="flex-1 rounded-lg border border-slate-200 px-3 py-2 text-sm"
                      value={selectedRecipientId || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value) || null;
                        setSelectedRecipientId(id);
                        if (id) loadConversation(id);
                      }}
                    >
                      <option value="">Select a student</option>
                      {messengerRecipients.map((recipient) => (
                        <option key={recipient.id} value={recipient.id}>
                          {recipient.firstName || recipient.lastName
                            ? `${recipient.firstName || ''} ${recipient.lastName || ''}`.trim()
                            : recipient.email}
                        </option>
                      ))}
                    </select>
                  </label>

                  <div className="rounded-xl border border-slate-200 bg-white">
                    <div className="px-3 py-2 text-xs font-semibold text-slate-500 uppercase tracking-wide border-b border-slate-100">
                      Chats
                    </div>
                    <div className="max-h-48 overflow-y-auto divide-y divide-slate-100">
                      {messengerThreads.length === 0 && (
                        <div className="px-3 py-2 text-sm text-slate-500">No chats yet.</div>
                      )}
                      {messengerThreads.map(thread => {
                        const isSelected = thread.userId === selectedRecipientId;
                        const unread = thread.unread || 0;
                        const label = thread.user?.firstName || thread.user?.lastName
                          ? `${thread.user?.firstName || ''} ${thread.user?.lastName || ''}`.trim()
                          : (thread.user?.email || 'Unknown user');
                        return (
                          <button
                            key={thread.userId}
                            onClick={() => {
                              setSelectedRecipientId(thread.userId);
                              loadConversation(thread.userId);
                            }}
                            className={`w-full px-3 py-2 text-left text-sm flex items-center justify-between gap-2 ${isSelected ? 'bg-slate-100' : 'hover:bg-slate-50'}`}
                          >
                            <div className="flex flex-col">
                              <span className="font-medium text-slate-800">{label}</span>
                              {thread.lastMessage?.text && (
                                <span className="text-xs text-slate-500 line-clamp-1">
                                  {thread.lastMessage.text}
                                </span>
                              )}
                            </div>
                            {unread > 0 && (
                              <span className="ml-auto inline-flex h-5 min-w-[20px] items-center justify-center rounded-full bg-red-500 px-2 text-[11px] font-semibold text-white">
                                {unread}
                              </span>
                            )}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                </div>
              </div>
            )}

            <div className="flex-1 overflow-hidden flex flex-col gap-3 p-4">
              {messengerError && (
                <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
                  {messengerError}
                </div>
              )}
              <div className="relative flex-1 overflow-y-auto rounded-xl border border-slate-200 bg-slate-50 p-3 space-y-3">
                {messengerMessages.length === 0 ? (
                  messengerLoading ? (
                    <div className="text-sm text-slate-500">Loading messages...</div>
                  ) : (
                    <div className="text-sm text-slate-500">No messages yet.</div>
                  )
                ) : (
                  messengerMessages.map((msg) => {
                    const myId = user?.id || user?.userId;
                    const isMine = msg.fromUserId === myId;
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isMine ? 'justify-end' : 'justify-start'}`}
                      >
                        <div className={`max-w-[80%] rounded-2xl px-3 py-2 text-sm shadow-sm ${isMine ? 'bg-slate-800 text-white' : 'bg-white border border-slate-200 text-slate-900'}`}>
                          <div className="whitespace-pre-wrap break-words">{msg.text}</div>
                          <div className={`mt-1 text-[11px] ${isMine ? 'text-slate-200' : 'text-slate-500'}`}>
                            {new Date(msg.timestamp).toLocaleString()}
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>
              <form
                onSubmit={(e) => { e.preventDefault(); handleSendMessage(); }}
                className="flex items-center gap-3"
              >
                <textarea
                  rows={2}
                  className="flex-1 resize-none rounded-xl border border-slate-200 px-3 py-2 text-sm focus:border-slate-400 focus:outline-none"
                  placeholder="Type a message..."
                  value={messengerText}
                  onChange={(e) => setMessengerText(e.target.value)}
                />
                <button
                  type="submit"
                  className="rounded-xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white hover:bg-slate-900 disabled:opacity-50"
                  disabled={!messengerText.trim()}
                >
                  Send
                </button>
              </form>
            </div>
          </div>
        </div>
      )}

      {/* Auth modal - moved here to appear centered on screen */}
      {showAuth && (
        <AuthModal
          initialMode={authMode}
          initialReferralCode={referralCodeFromUrl}
          onClose={() => setShowAuth(false)}
          onAuthed={(session) => {
            if (session && session.user) {
              setUser(session.user);
              persistUserProfile(session.user);
            }

            if (session && session.token) {
              persistAccessToken(session.token);
            }

            setShowAuth(false);
          }}
        />
      )}

      {/* Main Content - Show Dashboard if logged in, otherwise show home page */}
      {user ? (
        user.role === "ADMIN" ? (
          <AdminDashboard t={t} user={user} unreadEnrollments={unreadEnrollments} setUnreadEnrollments={setUnreadEnrollments} />
        ) : user.role === "INSTRUCTOR" ? (
          <InstructorDashboard
            t={t}
            user={user}
            openTrainingProgress={() => setShowInstructorTrainingProgress(true)}
            openStudyMaterials={() => setShowInstructorStudyMaterials(true)}
            openStudentDocuments={() => setShowInstructorStudentDocuments(true)}
            openTrainingCalendar={() => setShowInstructorTrainingCalendar(true)}
          />
        ) : (
          <StudentDashboard t={t} user={user} onPermitClick={() => setShowExamPractice(true)} />
        )
      ) : (
        <>
          {timerLoaded && timerSettings?.timer_enabled && timerSettings?.target_date && (
            <CountdownTimer
              targetDate={timerSettings.target_date}
              text={timerSettings.timer_text || 'Next CDL class starts in:'}
              style={timerSettings.style_json}
              onExpireAction={timerSettings.expire_action}
              className="py-6 sm:py-8"
            />
          )}

          {/* Hero: centered image with overlayed text + button */}
          <section id="home" className="relative flex justify-center text-center py-12">
            <div className="relative w-full max-w-7xl">
              <img
                src="/images/driver.jpg"
                alt="Smiling CDL driver"
                className="w-full rounded-2xl object-cover"
              />
              <div className="absolute inset-0 bg-black/20 flex flex-col items-start justify-center px-8 sm:px-16">
                <motion.h1
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6 }}
                  className="text-3xl sm:text-5xl md:text-7xl font-extrabold tracking-tight text-white drop-shadow-lg leading-tight"
                >
                  {t.hero.titleTop}
                  <br className="block sm:hidden" />
                  <br className="hidden sm:block" />
                  <span className="whitespace-normal sm:whitespace-nowrap">{t.hero.titleBottom}</span>
                </motion.h1>
                <p className="mt-4 sm:mt-6 max-w-lg text-lg sm:text-xl md:text-2xl text-white/95 font-medium leading-relaxed">
                  {t.hero.subtitle}
                </p>
                <button
                  onClick={() => { setAuthMode("signup"); setShowAuth(true); }}
                  className="mt-6 sm:mt-8 rounded-2xl sm:rounded-3xl bg-red-600 px-6 sm:px-8 md:px-10 py-3 sm:py-4 md:py-5 text-lg sm:text-xl md:text-2xl font-bold text-white shadow-xl hover:bg-red-700 transition-colors"
                >
                  {t.hero.cta}
                </button>
              </div>
            </div>
          </section>

          {/* Feature cards */}
          <section id="features" className="mx-auto max-w-7xl px-4 py-16">
            <div className="grid gap-8 md:grid-cols-3">
              <FeatureCard title={t.features.a.title} desc={t.features.a.desc} image="/images/instructor.jpg" />
              <FeatureCard title={t.features.b.title} desc={t.features.b.desc} image="/images/training.jpg" />
              <FeatureCard title={t.features.c.title} desc={t.features.c.desc} image="/images/placement.jpg" />
            </div>
          </section>

          {/* About */}
          <section id="about" className="bg-white">
            <div className="mx-auto max-w-4xl px-4 py-16 sm:px-6 lg:px-8 text-center">
              <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">{t.about.heading}</h2>
              <p className="mt-6 text-slate-600 leading-relaxed">{t.about.body}</p>
            </div>
          </section>

          {/* Contact */}
          <section id="contact" className="bg-slate-900">
            <div className="mx-auto max-w-7xl px-4 py-16 grid gap-10 lg:grid-cols-2">
              <div>
                <h2 className="text-3xl font-bold text-white sm:text-4xl">{t.contact.title}</h2>
                <p className="mt-3 text-slate-300">{t.contact.subtitle}</p>
                <ul className="mt-6 grid gap-3 text-slate-200">
                  {t.contact.bullets.map((b, i) => (
                    <li key={i} className="flex items-start gap-2">
                      <span className="mt-1 h-2.5 w-2.5 rounded-full bg-red-500" />{b}
                    </li>
                  ))}
                </ul>
              </div>

              {/* Enrollment form submission handler */}
              {(() => {
                const handleEnrollmentSubmit = async (e) => {
                  e.preventDefault();
                  const formData = new FormData(e.target);

                  const enrollmentData = {
                    firstName: formData.get("firstName"),
                    lastName: formData.get("lastName"),
                    phone: formData.get("phone"),
                    email: formData.get("email"),
                    program: formData.get("program"),
                    message: formData.get("message") || ""
                  };

                  try {
                    const response = await fetch(`${API_BASE}/api/enrollment/submit`, {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify(enrollmentData),
                    });

                    if (response.ok) {
                      await response.json(); // Consume response but don't use data
                      alert("Enrollment submitted successfully! We'll contact you within one business day.");
                      e.target.reset(); // Clear the form
                    } else {
                      const errorData = await response.json();
                      alert("Failed to submit enrollment: " + (errorData.error || "Unknown error"));
                    }
                  } catch (error) {
                    alert("Network error. Please try again.");
                  }
                };

                return <EnrollmentForm t={t} onSubmit={handleEnrollmentSubmit} />;
              })()}
            </div>
          </section>
        </>
      )}

      {/* Exam Practice Modal */}
      {showExamPractice && user && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50">
          <header className="sticky top-0 z-10 border-b border-slate-200 bg-white/95 backdrop-blur">
            <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-3 sm:h-20 sm:px-8">
              <div className="flex min-w-0 items-center gap-3 sm:gap-6">
                <img src="/images/logo.jpg" alt="Go2 CDL" className="h-8 w-20 object-contain sm:h-12 sm:w-28" />
                <div className="hidden h-8 w-px bg-slate-200 sm:block" />
                <h2 className="truncate text-lg font-bold tracking-tight text-slate-950 sm:text-3xl">CDL Permit Practice</h2>
              </div>
              <button type="button" onClick={() => setShowExamPractice(false)} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
            </div>
          </header>
          <main className="mx-auto w-full max-w-6xl px-3 py-3 pb-[calc(env(safe-area-inset-bottom,0px)+0.75rem)] sm:px-8 sm:py-8">
            <ExamPractice />
          </main>
        </div>
      )}

      {user && user.role === 'INSTRUCTOR' && showInstructorTrainingProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">Training Progress Management</h2>
              <button type="button" onClick={() => setShowInstructorTrainingProgress(false)} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
            </div>
            <AdminTrainingProgress />
          </div>
        </div>
      )}

      {user && user.role === 'INSTRUCTOR' && showInstructorStudyMaterials && (
        <StudyMaterialsModal onClose={() => setShowInstructorStudyMaterials(false)} />
      )}

      {user && user.role === 'INSTRUCTOR' && showInstructorStudentDocuments && (
        <StudentDocumentsModal onClose={() => setShowInstructorStudentDocuments(false)} />
      )}

      {user && user.role === 'INSTRUCTOR' && showInstructorTrainingCalendar && (
        <TrainingCalendarModal
          onClose={() => setShowInstructorTrainingCalendar(false)}
          user={user}
        />
      )}

      {/* Footer */}
      <footer className="border-t bg-white">
        <div className="mx-auto flex max-w-7xl flex-col items-center justify-between gap-4 px-4 py-8 sm:flex-row">
          <div className="text-sm text-slate-600">© {new Date().getFullYear()} Go2 CDL. All rights reserved.</div>
          <div className="flex items-center gap-6 text-sm">
            {/* Socials/links could go here */}
          </div>
        </div>
      </footer>
    </div>
  );
}
// FeatureCard component has been moved to src/components/ui/FeatureCard.js

// LangSelect component has been moved to src/components/ui/LangSelect.js

// AuthModal component has been moved to src/components/auth/AuthModal.js



// ---- Exam Practice Component ----
function shuffleArray(input) {
  const array = Array.isArray(input) ? [...input] : [];
  for (let i = array.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [array[i], array[j]] = [array[j], array[i]];
  }
  return array;
}

function prepareExamQuestion(question, { randomizeChoices }) {
  if (!question) return question;

  const baseChoices = Array.isArray(question.choices) ? [...question.choices] : [];
  const baseHighlights = Array.isArray(question.answerHighlights)
    ? [...question.answerHighlights]
    : question.answerHighlights;

  const identityOrder = baseChoices.map((_, index) => index);
  const choiceOrder = randomizeChoices ? shuffleArray(identityOrder) : identityOrder;

  const shuffledChoices = choiceOrder.map(index => baseChoices[index] ?? '');
  const shuffledHighlights = Array.isArray(baseHighlights)
    ? choiceOrder.map(index => baseHighlights[index] ?? '')
    : baseHighlights;

  const originalCorrectIndex = (typeof question.correctIndex === 'number' && question.correctIndex >= 0)
    ? question.correctIndex
    : 0;
  const remappedCorrectIndex = choiceOrder.findIndex(orderIndex => orderIndex === originalCorrectIndex);

  return {
    ...question,
    choices: shuffledChoices,
    answerHighlights: shuffledHighlights,
    choiceOrder,
    correctIndex: remappedCorrectIndex >= 0 ? remappedCorrectIndex : originalCorrectIndex,
  };
}

function ExamPractice() {
  const [categories, setCategories] = useState([]);
  const [category, setCategory] = useState("");
  const [questions, setQuestions] = useState([]);
  const [idx, setIdx] = useState(0);
  const [practiceMode, setPracticeMode] = useState(true); // true = Practice mode, false = Exam mode

  const [loadingCats, setLoadingCats] = useState(false);
  const [loadingQ, setLoadingQ] = useState(false);
  const [error, setError] = useState("");

  const [selected, setSelected] = useState(null);
  const [result, setResult] = useState(null);
  const [score, setScore] = useState({ correct: 0 }); // Only track correct answers
  const [questionResults, setQuestionResults] = useState([]); // Track results for each question
  const [showExplanation, setShowExplanation] = useState(false);


  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        setLoadingCats(true);
        setError("");
        const cats = await apiExamCategories();
        if (mounted) {
          setCategories(cats);
          if (cats?.length && !category) setCategory(cats[0]);
        }
      } catch (e) {
        setError(e.message || "Failed to load categories");
      } finally {
        setLoadingCats(false);
      }
    })();
    return () => { mounted = false; };
  }, [category]);

  useEffect(() => {
    if (!category) return;
    let mounted = true;
    (async () => {
      try {
        setLoadingQ(true);
        setError("");
        const qs = await apiExamQuestions(category);
        if (mounted) {
          const preparedQuestions = (qs || []).map(q =>
            prepareExamQuestion(q, { randomizeChoices: !practiceMode })
          );
          const finalQuestions = practiceMode
            ? preparedQuestions
            : shuffleArray(preparedQuestions);
          setQuestions(finalQuestions);
          setIdx(0);
          setSelected(null);
          setResult(null);
          setScore({ correct: 0 }); // Reset correct answers
          setQuestionResults([]); // Reset question results

        }
      } catch (e) {
        setError(e.message || "Failed to load questions");
      } finally {
        setLoadingQ(false);
      }
    })();
    return () => { mounted = false; };
  }, [category, practiceMode]);

  const current = questions[idx];
  const totalQuestions = questions.length;
  const safeTotalQuestions = totalQuestions || 1;
  const answeredCount = questionResults.filter((result) => result !== undefined).length;
  const progressPercentage = totalQuestions === 0
    ? 0
    : answeredCount > 0
      ? Math.round((answeredCount / safeTotalQuestions) * 100)
      : Math.round(((idx + 1) / safeTotalQuestions) * 100);

  const explanationText = (current?.explanation || '').trim();
  const explanationImageUrlRaw = current?.explanationImageUrl || '';
  const explanationImageUrl = explanationImageUrlRaw
    ? ((explanationImageUrlRaw.startsWith('http') || explanationImageUrlRaw.startsWith('data:'))
      ? explanationImageUrlRaw
      : `${API_BASE}${explanationImageUrlRaw}`)
    : null;
  const hasExplanation = Boolean(explanationText || explanationImageUrl);

  // Debug: Log the current question object to see its structure
  useEffect(() => {
    if (current) {
      console.log('Current question object:', {
        id: current.id,
        text: current.text,
        choices: current.choices,
        correctIndex: current.correctIndex,
        correct: current.correct,
        allProperties: Object.keys(current)
      });
    }
  }, [current]);

  useEffect(() => {
    setShowExplanation(false);
  }, [idx, current?.id]);

  useEffect(() => {
    if (!hasExplanation && showExplanation) {
      setShowExplanation(false);
    }
  }, [hasExplanation, showExplanation]);

  async function submitAnswer(choiceIndex) {
    if (!current) return;
    setSelected(choiceIndex);
    try {
      const originalChoiceIndex = Array.isArray(current.choiceOrder)
        ? current.choiceOrder[choiceIndex]
        : choiceIndex;

      const response = await apiExamAnswer(current.id, originalChoiceIndex);

      const mappedCorrectIndex = Array.isArray(current.choiceOrder)
        ? current.choiceOrder.findIndex(originalIdx => originalIdx === response.correctIndex)
        : response.correctIndex;

      const normalizedResult = {
        ...response,
        correctIndex: mappedCorrectIndex >= 0 ? mappedCorrectIndex : response.correctIndex,
      };

      setResult(normalizedResult);
      setScore(s => ({
        correct: s.correct + (normalizedResult.correct ? 1 : 0),
      }));

      // Track the result for this specific question
      setQuestionResults(prev => {
        const newResults = [...prev];
        newResults[idx] = normalizedResult.correct;
        return newResults;
      });
    } catch (e) {
      setError(e.message || "Failed to submit answer");
    }
  }

  function nextQuestion() {
    if (idx < questions.length - 1) {
      setIdx(idx + 1);
      setSelected(null);
      setResult(null);
    }
  }

  function prevQuestion() {
    if (idx > 0) {
      setIdx(idx - 1);
      setSelected(null);
      setResult(null);
    }
  }

  return <div className="space-y-3 sm:space-y-6">
    <div className="inline-flex w-full max-w-md overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm sm:w-auto sm:rounded-2xl">
      <button type="button" onClick={() => setPracticeMode(true)} className={`flex flex-1 items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold transition-colors sm:flex-none sm:px-8 sm:py-3 sm:text-base ${practiceMode ? 'bg-blue-600 text-white shadow-inner' : 'text-slate-700 hover:bg-slate-50'}`}><span aria-hidden="true">▤</span> Practice</button>
      <button type="button" onClick={() => setPracticeMode(false)} className={`flex flex-1 items-center justify-center gap-2 border-l border-slate-200 px-4 py-2.5 text-sm font-semibold transition-colors sm:flex-none sm:px-8 sm:py-3 sm:text-base ${!practiceMode ? 'bg-blue-600 text-white shadow-inner' : 'text-slate-700 hover:bg-slate-50'}`}><span aria-hidden="true">☑</span> Exam</button>
    </div>

    <section className="grid grid-cols-2 gap-2 sm:gap-3">
      <label className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:rounded-2xl sm:p-4"><span className="block text-xs font-medium text-slate-700 sm:text-sm">Topic</span><select value={category} onChange={(event) => setCategory(event.target.value)} className="mt-2 w-full truncate appearance-none bg-transparent text-sm font-semibold text-slate-950 outline-none sm:mt-3 sm:text-lg" disabled={loadingCats}>{categories.map(item => <option key={item} value={item}>{item}</option>)}</select></label>
      <div className="min-w-0 rounded-xl border border-slate-200 bg-white p-3 shadow-sm sm:rounded-2xl sm:p-4"><span className="block text-xs font-medium text-slate-700 sm:text-sm">Progress</span><div className="mt-3 flex items-center gap-2 sm:mt-5 sm:gap-3"><div className="h-2 flex-1 overflow-hidden rounded-full bg-slate-200"><div className="h-full rounded-full bg-blue-600 transition-all" style={{ width: `${progressPercentage}%` }} /></div><span className="text-sm font-semibold text-blue-600 sm:text-lg">{progressPercentage}%</span></div></div>
    </section>

    {error && <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">{error}</div>}
    {loadingQ && <div className="rounded-xl border border-slate-200 bg-white px-4 py-8 text-center text-sm text-slate-500">Loading questions…</div>}

    {current && <>
      <section className="rounded-xl bg-white sm:rounded-2xl sm:px-1">
        <div className="flex items-start gap-3 py-1 sm:gap-5 sm:py-2"><span className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-blue-600 text-base font-bold text-white shadow-lg shadow-blue-600/20 sm:h-16 sm:w-16 sm:rounded-xl sm:text-xl">Q{idx + 1}</span><div className="min-w-0 text-base font-medium leading-snug text-slate-900 sm:pt-1 sm:text-2xl sm:leading-relaxed" dangerouslySetInnerHTML={{ __html: (practiceMode && current.questionHighlight) ? highlightText(current.text, current.questionHighlight, 'adminHighlight') : current.text }} /></div>
        <ul className="mt-3 space-y-2 sm:mt-5 sm:space-y-3">{current.choices.map((choice, choiceIndex) => {
          const isPicked = selected === choiceIndex;
          const isCorrect = result?.correctIndex === choiceIndex;
          let choiceClass = 'border-slate-200 bg-white text-slate-900 hover:border-blue-300 hover:bg-blue-50/40';
          if (result !== null && isCorrect) choiceClass = 'border-green-400 bg-green-50 text-green-950';
          else if (result !== null && isPicked) choiceClass = 'border-red-400 bg-red-50 text-red-950';
          else if (isPicked) choiceClass = 'border-blue-600 bg-blue-50 text-slate-950 ring-1 ring-blue-600';
          return <li key={choiceIndex}><button type="button" disabled={result !== null} onClick={() => submitAnswer(choiceIndex)} className={`flex w-full items-center gap-3 rounded-xl border p-2.5 text-left text-sm font-medium transition-colors disabled:cursor-default sm:gap-4 sm:rounded-2xl sm:p-4 sm:text-lg ${choiceClass}`}><span className={`grid h-8 w-8 shrink-0 place-items-center rounded-full text-sm font-semibold sm:h-10 sm:w-10 sm:text-lg ${isPicked && result === null ? 'bg-blue-600 text-white' : isCorrect && result !== null ? 'bg-green-600 text-white' : 'bg-slate-100 text-slate-700'}`}>{isCorrect && result !== null ? '✓' : String.fromCharCode(65 + choiceIndex)}</span><span className="flex-1" dangerouslySetInnerHTML={{ __html: (practiceMode && current.answerHighlights && current.answerHighlights[choiceIndex]) ? highlightText(choice, current.answerHighlights[choiceIndex], 'adminHighlight') : choice }} />{isPicked && result === null && <span className="grid h-7 w-7 place-items-center rounded-full bg-blue-600 text-sm text-white sm:h-8 sm:w-8">✓</span>}</button></li>;
        })}</ul>

        {result !== null && <div className={`mt-4 rounded-xl border px-4 py-3 text-sm ${result.correct ? 'border-green-200 bg-green-50 text-green-800' : 'border-red-200 bg-red-50 text-red-800'}`}>{result.correct ? 'Correct.' : 'Incorrect.'} The correct answer is <strong dangerouslySetInnerHTML={{ __html: (practiceMode && current.answerHighlights && current.answerHighlights[result.correctIndex]) ? highlightText(current.choices[result.correctIndex], current.answerHighlights[result.correctIndex], 'adminHighlight') : current.choices[result.correctIndex] }} />.</div>}

        {practiceMode && hasExplanation && <section className="mt-3 overflow-hidden rounded-xl border border-slate-200 bg-slate-50 sm:mt-5 sm:rounded-2xl"><button type="button" onClick={() => setShowExplanation(value => !value)} className="flex w-full items-center gap-3 px-3 py-3 text-left sm:px-4 sm:py-4"><span className="grid h-8 w-8 place-items-center rounded-full bg-blue-600 text-base text-white sm:h-10 sm:w-10 sm:text-xl">☼</span><span className="flex-1 text-sm font-semibold text-blue-700 sm:text-lg">Explanation</span><span className="text-lg text-blue-600 sm:text-xl">{showExplanation ? '⌃' : '⌄'}</span></button>{showExplanation && <div className="border-t border-slate-200 px-3 pb-4 pt-3 sm:px-4 sm:pb-5 sm:pt-4"><p className="whitespace-pre-line text-sm leading-relaxed text-slate-700 sm:text-base">{explanationText || 'Review the study image and question details before moving to the next question.'}</p>{explanationImageUrl && <img src={explanationImageUrl} alt="Question explanation" className="mt-3 max-h-72 rounded-xl border border-slate-200 bg-white object-contain sm:mt-4" />}</div>}</section>}
      </section>

      <nav className="flex items-center justify-between gap-3 pt-0.5 sm:pt-1"><button type="button" onClick={prevQuestion} disabled={idx === 0} className="inline-flex items-center gap-2 rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm transition-colors hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40 sm:px-6 sm:py-3 sm:text-base">← <span className="hidden sm:inline">Previous</span></button><span className="text-xs font-semibold text-slate-700 sm:text-base">{idx + 1} / {totalQuestions || 1}</span><button type="button" onClick={nextQuestion} disabled={idx >= questions.length - 1} className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-3 py-2 text-sm font-semibold text-white shadow-lg shadow-blue-600/20 transition-colors hover:bg-blue-700 disabled:cursor-not-allowed disabled:bg-blue-200 disabled:shadow-none sm:px-6 sm:py-3 sm:text-base"><span className="hidden sm:inline">Next</span> →</button></nav>
    </>}

    {!loadingQ && questions.length === 0 && <div className="rounded-xl border border-dashed border-slate-300 bg-white px-4 py-10 text-center text-sm text-slate-500">No questions are available for this topic.</div>}
  </div>;
}

// ---- Student Dashboard Component ----
function StudentDashboard({ t, user, onPermitClick }) {
  const [showDocuments, setShowDocuments] = useState(false);
  const [showStudyMaterials, setShowStudyMaterials] = useState(false);
  const [showTrainingProgress, setShowTrainingProgress] = useState(false);
  const [showTrainingSchedule, setShowTrainingSchedule] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [paymentSummary, setPaymentSummary] = useState(null);
  const [documentSummary, setDocumentSummary] = useState(() => buildDocumentRequirementsSummary());
  const userId = user?.id;

  const refreshPaymentSummary = useCallback(async () => {
    if (!userId) return;
    try {
      const response = await paymentsApi.getStudentSummary();
      setPaymentSummary(response.summary);
    } catch (error) {
      console.error('Failed to load student payment summary:', error);
    }
  }, [userId]);

  const refreshDocumentSummary = useCallback(async () => {
    if (!userId) {
      setDocumentSummary(buildDocumentRequirementsSummary());
      return;
    }
    try {
      const response = await fetch(`${API_BASE}/api/documents/my`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status}`);
      }

      const docs = await response.json();
      setDocumentSummary(buildDocumentRequirementsSummary(docs));
    } catch (error) {
      console.error('Failed to load document summary:', error);
      setDocumentSummary(buildDocumentRequirementsSummary());
    }
  }, [userId]);

  useEffect(() => {
    refreshPaymentSummary();
  }, [refreshPaymentSummary]);

  useEffect(() => {
    refreshDocumentSummary();
  }, [refreshDocumentSummary]);

  const outstandingBalance = paymentSummary?.outstanding ?? 0;
  const documentsNeedAttention = studentDocumentsRequireAttention(documentSummary);

  const handleOpenPayments = useCallback(() => {
    setShowPayments(true);
  }, []);

  const handleClosePayments = useCallback(() => {
    setShowPayments(false);
    refreshPaymentSummary();
  }, [refreshPaymentSummary]);

  const studentCopy = t?.student || {};
  const studentHeaderCopy = studentCopy.header || {};
  const studentModalCopy = studentCopy.modals || {};

  const studentHeaderTitle = studentHeaderCopy.default || t.dashboard.title;

  const handleDocumentsUpdated = useCallback((docs) => {
    if (Array.isArray(docs)) {
      setDocumentSummary(buildDocumentRequirementsSummary(docs));
    }
  }, []);

  return (
    <div className="mx-auto max-w-7xl px-4 py-16">
      <div className="grid gap-6 grid-cols-2 md:grid-cols-2 lg:grid-cols-3">
        {getOrderedStudentSections(t).map((section) => {
          const getClickHandler = () => {
            switch (section.id) {
              case "permit": return onPermitClick;
              case "schedule": return () => setShowTrainingSchedule(true);
              case "progress": return () => setShowTrainingProgress(true);
              case "documents": return () => setShowDocuments(true);
              case "study-materials": return () => setShowStudyMaterials(true);
              case "payments": return handleOpenPayments;
              default: return () => { };
            }
          };

          const variant = (() => {
            if (section.id === "payments" && outstandingBalance > 0) {
              return 'danger';
            }
            if (section.id === "documents" && documentsNeedAttention) {
              return 'danger';
            }
            return 'default';
          })();

          return (
            <DashboardButton
              key={section.id}
              title={section.title}
              description={section.description}
              onClick={getClickHandler()}
              icon={section.icon}
              variant={variant}
            />
          );
        })}
      </div>

      {/* Documents Modal */}
      {showDocuments && (
        <DocumentsModal
          onClose={() => setShowDocuments(false)}
          copy={t.student?.modals?.documents?.copy}
          onDocumentsUpdated={handleDocumentsUpdated}
        />
      )}

      {/* Training Progress Modal */}
      {showTrainingProgress && (
        <div className="fixed inset-0 z-50 overflow-y-auto bg-slate-50">
          <TrainingProgress
            userId={user.id}
            copy={t.student?.modals?.trainingProgress?.training}
            onClose={() => setShowTrainingProgress(false)}
          />
        </div>
      )}

      {/* Study Materials Modal */}
      {showStudyMaterials && (
        <StudentStudyMaterialsModal
          onClose={() => setShowStudyMaterials(false)}
          copy={t.student?.modals?.studyMaterials?.student}
        />
      )}

      {showTrainingSchedule && (
        <TrainingScheduleModal
          user={user}
          onClose={() => setShowTrainingSchedule(false)}
          copy={t.student?.modals?.schedule?.schedule}
        />
      )}

      {showPayments && (
        <StudentPaymentsModal
          user={user}
          onClose={handleClosePayments}
          onSummaryChange={setPaymentSummary}
          copy={studentModalCopy.payments?.copy}
        />
      )}
    </div>
  );
}

// DashboardButton component has been moved to src/components/dashboard/DashboardButton.js

// ---- Admin Dashboard Component ----
function AdminDashboard({ t, user, unreadEnrollments, setUnreadEnrollments }) {
  console.log('🎯 AdminDashboard received unreadEnrollments:', unreadEnrollments);

  const [stats, setStats] = useState({
    totalUsers: 0,
    completedStudents: 0,
    activeSessions: 0
  });
  const [showUserManagement, setShowUserManagement] = useState(false);
  const [showQuestionBank, setShowQuestionBank] = useState(false);
  const [showDocuments, setShowDocuments] = useState(false);
  const [showStudyMaterials, setShowStudyMaterials] = useState(false);
  const [showTrainingProgress, setShowTrainingProgress] = useState(false);
  const [showStudentDocuments, setShowStudentDocuments] = useState(false);
  const [showEnrollmentMessages, setShowEnrollmentMessages] = useState(false);
  const [showAppointmentManagement, setShowAppointmentManagement] = useState(false);
  const [showTrainingCalendar, setShowTrainingCalendar] = useState(false);
  const [showSystemSettings, setShowSystemSettings] = useState(false);
  const [showPayments, setShowPayments] = useState(false);
  const [showContentManagement, setShowContentManagement] = useState(false);
  const [pendingDocumentReviews, setPendingDocumentReviews] = useState(0);
  const [pendingAppointments, setPendingAppointments] = useState(0);
  const [showActiveSessionsModal, setShowActiveSessionsModal] = useState(false);
  const [activeSessionsData, setActiveSessionsData] = useState({
    sessions: [],
    students: [],
    logins: [],
    fetchedAt: null,
    range: null,
  });
  const [loadingActiveSessions, setLoadingActiveSessions] = useState(false);
  const [activeSessionsError, setActiveSessionsError] = useState('');

  const adminCopy = t?.admin || {};
  const headerCopy = adminCopy.header || {};
  const statsCopy = adminCopy.stats || {};
  const messagesCopy = adminCopy.messages || {};
  const sectionCopy = adminCopy.sections || {};
  const modalCopy = adminCopy.modals || {};

  const headerSubtitle = headerCopy.subtitle || 'Manage your CDL training platform';

  const totalUsersLabel = statsCopy.totalUsers || 'Total Users';
  const completedStudentsLabel = statsCopy.completedStudents || 'CDL Course Completed';
  const activeSessionsLabel = statsCopy.activeSessions || 'Active Sessions';

  const updatePendingDocuments = useCallback((docs) => {
    if (Array.isArray(docs)) {
      setPendingDocumentReviews(countPendingRequiredDocuments(docs));
    }
  }, []);

  const updatePendingAppointments = useCallback((appointments) => {
    if (Array.isArray(appointments)) {
      setPendingAppointments(countPendingAppointments(appointments));
    }
  }, []);

  const loadActiveSessions = useCallback(async () => {
    try {
      setLoadingActiveSessions(true);
      setActiveSessionsError('');

      const response = await fetch(`${API_BASE}/api/admin/active-sessions/today`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch active sessions (${response.status})`);
      }

      const data = await response.json();

      const normalizedSessions = Array.isArray(data.sessions)
        ? data.sessions.map((session) => ({
          ...session,
          durationMinutes: Number(session.durationMinutes ?? 0),
          start: session.start ? new Date(session.start).toISOString() : null,
          end: session.end ? new Date(session.end).toISOString() : null,
        }))
        : [];

      const normalizedLogins = Array.isArray(data.logins)
        ? data.logins.map((login) => ({
          ...login,
          lastLoginAt: login.lastLoginAt ? new Date(login.lastLoginAt).toISOString() : null,
        }))
        : [];

      const normalizedStudents = Array.isArray(data.students)
        ? data.students.map((student) => ({
          ...student,
          totalMinutes: Number(student.totalMinutes ?? 0),
          sections: Array.isArray(student.sections)
            ? student.sections.map((section) => ({
              ...section,
              minutes: Number(section.minutes ?? 0),
            }))
            : [],
          lastLoginAt: student.lastLoginAt ? new Date(student.lastLoginAt).toISOString() : null,
        }))
        : [];

      setActiveSessionsData({
        sessions: normalizedSessions,
        students: normalizedStudents,
        logins: normalizedLogins,
        fetchedAt: new Date().toISOString(),
        range:
          data.startOfDay && data.endOfDay
            ? { start: data.startOfDay, end: data.endOfDay }
            : null,
      });

      setStats((prev) => ({
        ...prev,
        activeSessions: Number(data.count || 0),
      }));
    } catch (error) {
      console.error('Failed to load active sessions details:', error);
      setActiveSessionsError(error?.message || 'Failed to load active sessions details');
    } finally {
      setLoadingActiveSessions(false);
    }
  }, []);

  const fetchPendingDocuments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/documents`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch documents: ${response.status}`);
      }

      const docs = await response.json();
      updatePendingDocuments(docs);
    } catch (error) {
      console.error('Failed to load pending documents:', error);
    }
  }, [updatePendingDocuments]);

  const fetchPendingAppointments = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/admin/appointments`, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
        },
      });

      if (!response.ok) {
        throw new Error(`Failed to fetch appointments: ${response.status}`);
      }

      const data = await response.json();
      updatePendingAppointments(data);
    } catch (error) {
      console.error('Failed to load pending appointments:', error);
    }
  }, [updatePendingAppointments]);

  const handleOpenActiveSessions = useCallback(() => {
    setShowActiveSessionsModal(true);

    const fetchedAt = activeSessionsData?.fetchedAt ? new Date(activeSessionsData.fetchedAt) : null;
    const shouldRefresh =
      !Array.isArray(activeSessionsData?.sessions) ||
      !Array.isArray(activeSessionsData?.logins) ||
      (activeSessionsData.sessions.length === 0 && activeSessionsData.logins.length === 0) ||
      !fetchedAt ||
      (Date.now() - fetchedAt.getTime()) > 5 * 60 * 1000;

    if (shouldRefresh && !loadingActiveSessions) {
      loadActiveSessions();
    }
  }, [activeSessionsData, loadActiveSessions, loadingActiveSessions]);

  const handleCloseActiveSessions = useCallback(() => {
    setShowActiveSessionsModal(false);
  }, []);

  const handleRefreshActiveSessions = useCallback(() => {
    loadActiveSessions();
  }, [loadActiveSessions]);

  useEffect(() => {
    // Fetch admin statistics
    const fetchStats = async () => {
      try {
        // Fetch real user count
        const usersResponse = await fetch(`${API_BASE}/api/users`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
          },
        });
        let totalUsers = 0;
        if (usersResponse.ok) {
          const usersData = await usersResponse.json();
          totalUsers = usersData.users ? usersData.users.length : 0;
        }

        // Fetch CDL completion statistics
        const completionResponse = await fetch(`${API_BASE}/api/training-progress/summary`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem('go2cdl_access_token')}`,
          },
        });
        let completedStudents = 0;
        if (completionResponse.ok) {
          const completionData = await completionResponse.json();
          completedStudents = completionData.summary?.fullyCertified || 0;
        }

        setStats((prev) => ({
          ...prev,
          totalUsers,
          completedStudents,
        }));

        await loadActiveSessions();
      } catch (error) {
        console.error('Failed to fetch admin stats:', error);
        // Fallback to showing loading state
        setStats({
          totalUsers: 0,
          completedStudents: 0,
          activeSessions: 0
        });
      }
    };

    fetchStats();
  }, [loadActiveSessions]);

  useEffect(() => {
    fetchPendingDocuments();
  }, [fetchPendingDocuments]);

  useEffect(() => {
    fetchPendingAppointments();
  }, [fetchPendingAppointments]);

  return (
    <div className="mx-auto max-w-7xl px-4 py-16">
      {/* Statistics Cards */}
      <div className="grid gap-6 md:grid-cols-3 mb-12">
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="text-3xl font-bold text-blue-600 mb-2">{stats.totalUsers}</div>
          <div className="text-slate-600">{totalUsersLabel}</div>
        </div>
        <div className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm">
          <div className="text-3xl font-bold text-green-600 mb-2">{stats.completedStudents || 0}</div>
          <div className="text-slate-600">{completedStudentsLabel}</div>
        </div>
        <button
          type="button"
          onClick={handleOpenActiveSessions}
          className="bg-white rounded-2xl border border-slate-200 p-6 shadow-sm text-left transition focus:outline-none focus:ring-2 focus:ring-purple-500/70 hover:border-purple-200"
          title="View active session details"
        >
          <div className="text-3xl font-bold text-purple-600 mb-2">{stats.activeSessions}</div>
          <div className="text-slate-600 flex items-center justify-between">
            <span>{activeSessionsLabel}</span>
            <span className="text-xs font-medium text-purple-500">
              {loadingActiveSessions ? 'Refreshing...' : 'View'}
            </span>
          </div>
        </button>
      </div>

      <div className="grid gap-6 grid-cols-2 md:grid-cols-2 lg:grid-cols-3">
        {getOrderedAdminSections(sectionCopy).map((section) => {
          const getClickHandler = () => {
            switch (section.id) {
              case "user-management": return () => setShowUserManagement(true);
              case "question-bank": return () => setShowQuestionBank(true);
              case "training-progress": return () => setShowTrainingProgress(true);
              case "system-settings": return () => setShowSystemSettings(true);
              case "study-materials": return () => setShowStudyMaterials(true);
              case "student-documents": return () => setShowStudentDocuments(true);
              case "enrollment-messages": return () => setShowEnrollmentMessages(true);
              case "payments": return () => setShowPayments(true);
              case "content-management": return () => setShowContentManagement(true);
              case "training-calendar": return () => setShowTrainingCalendar(true);
              default: return () => { };
            }
          };

          const getSpecialProps = () => {
            if (section.id === "enrollment-messages") {
              return {
                isHighlighted: Number(unreadEnrollments) > 0,
                unreadCount: unreadEnrollments,
                "data-debug": `unreadEnrollments: ${unreadEnrollments}`
              };
            }
            if (section.id === "student-documents") {
              return {
                isHighlighted: pendingDocumentReviews > 0,
                unreadCount: pendingDocumentReviews,
                highlightColor: 'red',
              };
            }
            if (section.id === "training-calendar") {
              return {
                isHighlighted: pendingAppointments > 0,
                unreadCount: pendingAppointments,
                highlightColor: 'red',
              };
            }
            return {};
          };

          return (
            <AdminButton
              key={section.id}
              title={section.title}
              description={section.description}
              onClick={getClickHandler()}
              icon={section.icon}
              {...getSpecialProps()}
            />
          );
        })}
      </div>

      {/* User Management Modal */}
      {showUserManagement && (
        <UserManagementModal onClose={() => setShowUserManagement(false)} />
      )}

      {/* Question Bank Modal */}
      {showQuestionBank && (
        <QuestionBankModal onClose={() => setShowQuestionBank(false)} />
      )}

      {/* Documents Modal */}
      {showDocuments && (
        <DocumentsModal
          onClose={() => setShowDocuments(false)}
          copy={modalCopy.documents?.copy}
        />
      )}

      {/* Study Materials Modal */}
      {showStudyMaterials && (
        <StudyMaterialsModal
          onClose={() => setShowStudyMaterials(false)}
          copy={modalCopy.studyMaterials?.copy}
        />
      )}

      {/* Student Documents Modal */}
      {showStudentDocuments && (
        <StudentDocumentsModal
          onClose={() => setShowStudentDocuments(false)}
          copy={modalCopy.studentDocuments?.copy}
          onDocumentsUpdated={updatePendingDocuments}
        />
      )}

      {showPayments && (
        <AdminPaymentsModal
          onClose={() => setShowPayments(false)}
          copy={modalCopy.payments}
        />
      )}

      {/* Training Progress Modal */}
      {showTrainingProgress && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 max-w-6xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-2xl font-bold">{modalCopy.trainingProgress?.copy?.header?.title || 'Training Progress Management'}</h2>
              <button type="button" onClick={() => setShowTrainingProgress(false)} className="inline-flex shrink-0 items-center gap-2 rounded-2xl bg-slate-800 px-4 py-2 text-sm font-semibold text-white transition hover:bg-slate-900" aria-label="Back to dashboard"><span className="text-lg leading-none" aria-hidden="true">←</span> Back</button>
            </div>
            <AdminTrainingProgress
              copy={modalCopy.trainingProgress?.copy}
              trainingCopy={modalCopy.trainingProgress?.training || t.student?.modals?.trainingProgress?.training}
            />
          </div>
        </div>
      )}

      {/* Enrollment Messages Modal */}
      {showEnrollmentMessages && (
        <EnrollmentMessagesModal
          onClose={() => setShowEnrollmentMessages(false)}
          onEnrollmentUpdate={() => {
            // Refresh unread count when enrollment status changes
            if (user && user.role === 'ADMIN') {
              const fetchUnreadCount = async () => {
                try {
                  const response = await fetch(`${API_BASE}/api/enrollment`, {
                    headers: { Authorization: `Bearer ${getAccessToken()}` },
                    credentials: 'include',
                  });
                  if (response.ok) {
                    const enrollments = await response.json();
                    const unreadCount = enrollments.filter(e => e.status === 'NEW').length;
                    setUnreadEnrollments(unreadCount);
                  }
                } catch (error) {
                  console.error('Error fetching enrollments count:', error);
                }
              };
              fetchUnreadCount();
            }
          }}
        />
      )}

      {/* Appointment Management Modal */}
      {showAppointmentManagement && (
        <AppointmentManagementModal
          onClose={() => setShowAppointmentManagement(false)}
          onAppointmentsUpdated={updatePendingAppointments}
        />
      )}

      {/* Training Calendar Modal */}
      {showTrainingCalendar && (
        <TrainingCalendarModal
          onClose={() => setShowTrainingCalendar(false)}
          onAppointmentsUpdated={updatePendingAppointments}
          user={user}
        />
      )}

      {/* Active Sessions Modal */}
      {showActiveSessionsModal && (
        <ActiveSessionsModal
          label={activeSessionsLabel}
          data={activeSessionsData}
          loading={loadingActiveSessions}
          error={activeSessionsError}
          onRefresh={handleRefreshActiveSessions}
          onClose={handleCloseActiveSessions}
        />
      )}

      {/* System Settings Modal */}
      {showSystemSettings && (
        <SystemSettingsModal
          onClose={() => setShowSystemSettings(false)}
        />
      )}
      {/* Content Management Modal */}
      {showContentManagement && (
        <ContentManagementModal
          onClose={() => setShowContentManagement(false)}
        />
      )}
    </div>
  );
}
